import { useState } from 'react'
import type { Activity } from '../../pages/admin/catalogStore'

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

/**
 * An activity, or a sub-activity beneath an open top-level one. A new one takes
 * its code, name, and parent; once it exists the code and parent are shown as
 * they are and only the name changes.
 */
export default function ActivityForm({
  activity,
  parentId,
  parents,
  saving,
  error,
  onCreate,
  onRename,
  onCancel,
}: {
  activity?: Activity
  parentId?: string | null
  parents: Activity[]
  saving: boolean
  error: string | null
  onCreate: (values: { code: string; name: string; parentId: string | null }) => void
  onRename: (name: string) => void
  onCancel: () => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState(activity?.name ?? '')
  const [parent, setParent] = useState(parentId ?? '')

  const canSave = name.trim() !== '' && (activity ? name.trim() !== activity.name : /^[a-z0-9][a-z0-9-]{0,19}$/.test(code))

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label={activity ? `Edit ${activity.name}` : 'Create activity'}
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSave || saving) return
        if (activity) onRename(name.trim())
        else onCreate({ code, name: name.trim(), parentId: parent || null })
      }}
    >
      {activity && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-small">
          <dt className="text-ink-muted">Code</dt>
          <dd className="font-medium">{activity.code}</dd>
          <dt className="text-ink-muted">Under</dt>
          <dd className="font-medium">{activity.parentName || 'Top level'}</dd>
        </dl>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor="activity-name" className={LABEL}>
            Name
          </label>
          <input id="activity-name" type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} className={FIELD} />
        </div>
        {!activity && (
          <>
            <div>
              <label htmlFor="activity-code" className={LABEL}>
                Code
              </label>
              <input
                id="activity-code"
                type="text"
                maxLength={20}
                aria-describedby="activity-code-hint"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className={FIELD}
              />
              <p id="activity-code-hint" className="mt-1 text-caption text-ink-subtle">
                Lowercase letters, digits, and hyphens. It cannot be changed later.
              </p>
            </div>
            <div>
              <label htmlFor="activity-parent" className={LABEL}>
                Under
              </label>
              <select id="activity-parent" value={parent} onChange={(event) => setParent(event.target.value)} className={FIELD}>
                <option value="">Top level</option>
                {parents.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Saving…' : activity ? 'Save changes' : 'Create activity'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
